import { randomBytes } from "node:crypto";
import type { SpaceMeta } from "@wakeloop/protocol";
import type {
  AccountSpaceIndexItem,
  PrepareSetupBootstrapResult,
  PrepareSetupBootstrapStep,
} from "@wakeloop/schemas/types";
import { listAccountSpaceIndex } from "../config/account-space-index-api";
import {
  resolveGatewayHost,
  upsertProfileSpaceHistory,
} from "../config/profile-space-history-api";
import {
  ensureWakeLoopManagedAgentProfile,
  resolveWakeLoopProfile,
  type WakeLoopProfile,
} from "../config/wakeloop-profiles-api";
import { buildWakeLoopRequestContextFromProfile } from "../config/wakeloop-request-context";
import { resolveSetupFollowUpCliEntryCommand } from "../runtime/cli-entry-command";
import { createCliSpaceApi } from "../space/cli-space-api";
import { buildSpaceWebUrl } from "../space/web-url";
import { buildSetupAssistantBootstrapWelcomeMessage } from "./setup-assistant-bootstrap-context";

const WAKELOOP_ASSISTANT_MANAGED_ROLE = "wakeloop_assistant" as const;
const WELCOMING_SPACE_PURPOSE = "wakeloop_welcoming_space" as const;
const WELCOMING_SPACE_HISTORY_STATUS = "active" as const;
const WELCOME_MESSAGE_PURPOSE = "setup_bootstrap_welcome" as const;

export type SetupBootstrapStep = PrepareSetupBootstrapStep;
export type SetupBootstrapResult = PrepareSetupBootstrapResult;
type SetupBootstrapWarning = Extract<
  SetupBootstrapResult,
  { status: "completed" }
>["warnings"][number];
type BootstrapWelcomingSpace = {
  createdAt: string;
  creatorKind: "human" | "agent" | "system" | null;
  creatorProfileId: string | null;
  name: string;
  outcome: "created" | "reused";
  spaceId: string;
  updatedAt: string | null;
  url: string;
};

export async function runSetupFirstBootstrap(input: {
  gatewayUrl: string;
  selectedLocalAgentIds: readonly string[];
}): Promise<SetupBootstrapResult> {
  try {
    return await runSetupFirstBootstrapOrThrow(input);
  } catch (error) {
    const stepError = error instanceof SetupBootstrapStepError ? error : null;
    const cause = stepError?.causeValue ?? error;
    const failedStep = stepError?.step ?? "unknown";
    const message = toErrorMessage(cause);
    console.warn("[wakeloop.setup.bootstrap.failed]", {
      event: "cli.setup.bootstrap.failed",
      error: message,
      failedStep,
      selectedAgentIds: [...input.selectedLocalAgentIds],
    });
    return {
      status: "failed",
      diagnostics: {
        errorName: readErrorName(cause),
        selectedAgentIds: [...input.selectedLocalAgentIds],
      },
      failedStep,
      message,
      reason: "bootstrap_failed",
      retryCommand: `${resolveSetupFollowUpCliEntryCommand()} setup`,
    };
  }
}

async function runSetupFirstBootstrapOrThrow(input: {
  gatewayUrl: string;
  selectedLocalAgentIds: readonly string[];
}): Promise<Extract<SetupBootstrapResult, { status: "completed" }>> {
  const humanProfile = await runBootstrapStep(
    "human_profile",
    async () =>
      await resolveWakeLoopProfile({
        profileKind: "human",
      })
  );
  const userName = resolveBootstrapUserName(humanProfile);
  const assistantProfile = await runBootstrapStep(
    "assistant_profile",
    async () =>
      await ensureWakeLoopManagedAgentProfile({
        creatorProfileId: humanProfile.wakeLoopProfileId,
        managedRole: WAKELOOP_ASSISTANT_MANAGED_ROLE,
        profileName: buildSetupAssistantProfileName(userName),
      })
  );

  assertManagedAssistantProfile(assistantProfile);

  const humanRequestContext = buildWakeLoopRequestContextFromProfile({
    wakeLoopProfile: humanProfile,
  });
  const baseSpaceName = buildSetupWelcomingSpaceName(userName);
  const existingWelcomingSpace = await runBootstrapStep(
    "account_space_index",
    async () =>
      await resolveExistingWelcomingSpaceForAccount({
        gatewayUrl: input.gatewayUrl,
        humanProfile,
      })
  );
  const spaceApi = createCliSpaceApi(input.gatewayUrl);
  const welcomingSpace =
    existingWelcomingSpace ??
    (await runBootstrapStep(
      "space_create",
      async () =>
        await createSetupWelcomingSpace({
          baseSpaceName,
          gatewayUrl: input.gatewayUrl,
          humanProfile,
          humanRequestContext,
          spaceApi,
        })
    ));

  const membership = await runBootstrapStep(
    "space_membership",
    async () =>
      await spaceApi.addMembers({
        memberProfileIds: [assistantProfile.wakeLoopProfileId],
        requestContext: humanRequestContext,
        spaceId: welcomingSpace.spaceId,
      })
  );

  const welcomeText = buildSetupWelcomeMessage(assistantProfile.profileName);
  const welcomeMessage =
    welcomingSpace.outcome === "created"
      ? await runBootstrapStep("welcome_message", async () => {
          const assistantRequestContext =
            buildWakeLoopRequestContextFromProfile({
              wakeLoopProfile: assistantProfile,
            });
          return await spaceApi.postNormalMessage({
            meta: {
              purpose: WELCOME_MESSAGE_PURPOSE,
            },
            requestContext: assistantRequestContext,
            spaceId: welcomingSpace.spaceId,
            text: welcomeText,
          });
        })
      : null;

  const warnings = await syncBootstrapProfileSpaceHistory({
    assistantProfile,
    gatewayUrl: input.gatewayUrl,
    humanProfile,
    welcomingSpace,
  });

  return {
    status: "completed",
    assistantProfile: {
      wakeLoopProfileId: assistantProfile.wakeLoopProfileId,
      managedRole: WAKELOOP_ASSISTANT_MANAGED_ROLE,
      profileKind: "agent",
      profileName: assistantProfile.profileName,
    },
    binding: null,
    humanProfile: {
      wakeLoopProfileId: humanProfile.wakeLoopProfileId,
      profileKind: "human",
      profileName: humanProfile.profileName,
    },
    facts: {
      ownerUserId: humanProfile.ownerUserId,
      assistantProfileId: assistantProfile.wakeLoopProfileId,
      spaceId: welcomingSpace.spaceId,
      spaceUrl: welcomingSpace.url,
      createdAt: welcomingSpace.createdAt,
    },
    membership: {
      addedMemberIds: [...membership.addedMemberIds],
      alreadyMemberIds: [...membership.alreadyMemberIds],
      assistantProfileId: assistantProfile.wakeLoopProfileId,
      humanProfileId: humanProfile.wakeLoopProfileId,
    },
    retryCommand: null,
    warnings,
    welcomeMessage: welcomeMessage
      ? {
          signalId: welcomeMessage.id,
          text: welcomeText,
        }
      : null,
    welcomingSpace: {
      createdAt: welcomingSpace.createdAt,
      name: welcomingSpace.name,
      outcome: welcomingSpace.outcome,
      spaceId: welcomingSpace.spaceId,
      url: welcomingSpace.url,
    },
  };
}

export function buildSetupAssistantProfileName(userName: string): string {
  return `${userName}'s WakeLoop Assistant`;
}

export function buildSetupWelcomingSpaceName(userName: string): string {
  return `${userName}'s WakeLoop Welcoming Space`;
}

async function resolveExistingWelcomingSpaceForAccount(input: {
  gatewayUrl: string;
  humanProfile: WakeLoopProfile;
}): Promise<BootstrapWelcomingSpace | null> {
  const requestContext = buildWakeLoopRequestContextFromProfile({
    wakeLoopProfile: input.humanProfile,
  });
  const index = await listAccountSpaceIndex({
    baseUrl: input.gatewayUrl,
    includePurposes: true,
    limit: 500,
    requestContext,
    scope: "account",
    selectedProfileId: input.humanProfile.wakeLoopProfileId,
  });
  const item =
    [...index.pinnedItems, ...index.spaceItems].find((candidate) =>
      isReusableSetupWelcomingSpace({
        humanProfile: input.humanProfile,
        item: candidate,
      })
    ) ?? null;
  if (!item) {
    return null;
  }
  return toReusedWelcomingSpace({
    gatewayUrl: input.gatewayUrl,
    humanProfile: input.humanProfile,
    item,
  });
}

function isReusableSetupWelcomingSpace(input: {
  humanProfile: WakeLoopProfile;
  item: AccountSpaceIndexItem;
}): boolean {
  if (input.item.spacePurpose !== WELCOMING_SPACE_PURPOSE) {
    return false;
  }

  const creatorOwnerUserId = normalizeOptionalText(
    input.item.creatorOwnerUserId
  );
  if (creatorOwnerUserId) {
    return creatorOwnerUserId === input.humanProfile.ownerUserId;
  }

  return input.item.creatorProfileId === input.humanProfile.wakeLoopProfileId;
}

function assertManagedAssistantProfile(profile: WakeLoopProfile): void {
  if (profile.profileKind !== "agent") {
    throw new Error(
      `Auth returned a managed WakeLoop Assistant profile with profileKind ${profile.profileKind}.`
    );
  }
  if (profile.managedRole !== WAKELOOP_ASSISTANT_MANAGED_ROLE) {
    throw new Error(
      "Auth returned a managed WakeLoop Assistant profile without managedRole wakeloop_assistant."
    );
  }
}

async function createSetupWelcomingSpace(input: {
  baseSpaceName: string;
  gatewayUrl: string;
  humanProfile: WakeLoopProfile;
  humanRequestContext: ReturnType<
    typeof buildWakeLoopRequestContextFromProfile
  >;
  spaceApi: ReturnType<typeof createCliSpaceApi>;
}): Promise<BootstrapWelcomingSpace> {
  const createdSpace = await input.spaceApi.createSpace({
    accessMode: "protected",
    name: input.baseSpaceName,
    password: buildDefaultWelcomingSpacePassword(),
    purpose: WELCOMING_SPACE_PURPOSE,
    requestContext: input.humanRequestContext,
  });
  const createdSpaceName =
    normalizeOptionalText(createdSpace.meta?.name) ?? input.baseSpaceName;
  const createdAt = requireCreatedSpaceCreatedAt(createdSpace.meta);
  return {
    createdAt,
    creatorKind: resolveCreatedSpaceCreatorKind(createdSpace.meta) ?? "human",
    creatorProfileId:
      resolveCreatedSpaceCreatorProfileId(createdSpace.meta) ??
      input.humanProfile.wakeLoopProfileId,
    name: createdSpaceName,
    outcome: "created",
    spaceId: createdSpace.spaceId,
    updatedAt: normalizeOptionalText(createdSpace.meta?.updatedAt),
    url: buildSetupWelcomingSpaceUrl(input.gatewayUrl, createdSpace.spaceId),
  };
}

function toReusedWelcomingSpace(input: {
  gatewayUrl: string;
  humanProfile: WakeLoopProfile;
  item: AccountSpaceIndexItem;
}): BootstrapWelcomingSpace {
  return {
    createdAt: requireAccountIndexSpaceCreatedAt(input.item),
    creatorKind: input.item.creatorKind ?? null,
    creatorProfileId: input.item.creatorProfileId ?? null,
    name: input.item.spaceName,
    outcome: "reused",
    spaceId: input.item.spaceId,
    updatedAt: input.item.spaceUpdatedAt,
    url: buildSetupWelcomingSpaceUrl(input.gatewayUrl, input.item.spaceId),
  };
}

function buildSetupWelcomingSpaceUrl(
  gatewayUrl: string,
  spaceId: string
): string {
  // The app route resolves the browser account's current human profile. Do not
  // pin the setup CLI profile into a link that may be opened in another account.
  return buildSpaceWebUrl(gatewayUrl, spaceId, { surface: "app" });
}

function requireAccountIndexSpaceCreatedAt(
  item: AccountSpaceIndexItem
): string {
  const createdAt = normalizeOptionalText(item.spaceCreatedAt);
  if (!createdAt) {
    throw new Error(
      `Managed WakeLoop Welcoming Space ${item.spaceId} is missing spaceCreatedAt in the account Space index.`
    );
  }
  return createdAt;
}

export function buildSetupWelcomeMessage(agentName: string): string {
  return buildSetupAssistantBootstrapWelcomeMessage({ agentName });
}

function buildDefaultWelcomingSpacePassword(): string {
  return randomBytes(32).toString("base64url");
}

async function syncBootstrapProfileSpaceHistory(input: {
  assistantProfile: WakeLoopProfile;
  gatewayUrl: string;
  humanProfile: WakeLoopProfile;
  welcomingSpace: BootstrapWelcomingSpace;
}): Promise<SetupBootstrapWarning[]> {
  const gatewayHost = resolveGatewayHost(input.gatewayUrl);
  const lastJoinedAt = new Date().toISOString();

  const warnings = await Promise.all(
    [input.humanProfile, input.assistantProfile].map(async (profile) => {
      try {
        await upsertProfileSpaceHistory({
          wakeLoopProfileId: profile.wakeLoopProfileId,
          baseUrl: input.gatewayUrl,
          requestContext: buildWakeLoopRequestContextFromProfile({
            wakeLoopProfile: profile,
          }),
          spaceId: input.welcomingSpace.spaceId,
          body: {
            creatorKind: input.welcomingSpace.creatorKind ?? "human",
            creatorProfileId:
              input.welcomingSpace.creatorProfileId ??
              input.humanProfile.wakeLoopProfileId,
            gatewayHost,
            lastJoinedAt,
            spaceCreatedAt: input.welcomingSpace.createdAt,
            ...(input.welcomingSpace.updatedAt
              ? { spaceUpdatedAt: input.welcomingSpace.updatedAt }
              : {}),
            spaceName: input.welcomingSpace.name,
            status: WELCOMING_SPACE_HISTORY_STATUS,
          },
        });
        return null;
      } catch (error) {
        return {
          code: "profile_space_history_sync_failed",
          message: toErrorMessage(error),
          profileId: profile.wakeLoopProfileId,
        } satisfies SetupBootstrapWarning;
      }
    })
  );

  return warnings.filter((warning) => warning !== null);
}

function resolveCreatedSpaceCreatorProfileId(meta?: SpaceMeta): string | null {
  const creator = meta?.creator;
  if (!(creator && typeof creator === "object")) {
    return null;
  }
  return normalizeOptionalText(
    "profileId" in creator
      ? (creator as { profileId?: unknown }).profileId
      : undefined
  );
}

function resolveCreatedSpaceCreatorKind(
  meta?: SpaceMeta
): "human" | "agent" | "system" | null {
  const kind = normalizeOptionalText(meta?.creator?.kind);
  return kind === "human" || kind === "agent" || kind === "system"
    ? kind
    : null;
}

function requireCreatedSpaceCreatedAt(meta?: SpaceMeta): string {
  const createdAt = normalizeOptionalText(meta?.createdAt);
  if (!createdAt) {
    throw new Error(
      "Space creation did not return meta.createdAt for setup bootstrap linkage."
    );
  }
  return createdAt;
}

function resolveBootstrapUserName(humanProfile: WakeLoopProfile): string {
  return requireNormalizedText(
    normalizeOptionalText(humanProfile.ownerName) ??
      normalizeOptionalText(humanProfile.profileName),
    "signed-in user name"
  );
}

function runBootstrapStep<T>(
  step: SetupBootstrapStep,
  run: () => Promise<T>
): Promise<T> {
  return run().catch((error: unknown) => {
    throw new SetupBootstrapStepError(step, error);
  });
}

class SetupBootstrapStepError extends Error {
  readonly causeValue: unknown;
  readonly step: SetupBootstrapStep;

  constructor(step: SetupBootstrapStep, causeValue: unknown) {
    super(`${step}: ${toErrorMessage(causeValue)}`);
    this.name = "SetupBootstrapStepError";
    this.step = step;
    this.causeValue = causeValue;
  }
}

function requireNormalizedText(value: unknown, label: string): string {
  const normalized = normalizeOptionalText(value);
  if (!normalized) {
    throw new Error(`${label} is required.`);
  }
  return normalized;
}

function normalizeOptionalText(value: unknown): string | null {
  const normalized = String(value ?? "").trim();
  return normalized.length > 0 ? normalized : null;
}

function toErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }
  if (typeof error === "string" && error.trim().length > 0) {
    return error;
  }
  return "Unknown setup bootstrap error.";
}

function readErrorName(error: unknown): string | null {
  return error instanceof Error && error.name.trim().length > 0
    ? error.name
    : null;
}
